import type { ReactNode } from "react";
import { Link } from "react-router";
import { Check } from "lucide-react";
import { Container, Section, Heading, Lede, Eyebrow, LinkButton } from "../../components/ui";
import { Seo } from "../../lib/head";
import {
  FaqSection,
  CtaBand,
  RelatedLinks,
  faqPageJsonLd,
  breadcrumbNode,
  jsonLdGraph,
  SOFTWARE_NODE,
  ORGANIZATION_NODE,
  type Faq,
} from "../../components/marketing";
import { COMPARE_PAGES, comparePath } from "./competitors";

/*
 * Shared shell for /compare/* pages. Each page supplies its OWN copy (answer,
 * criteria, sections, FAQs) so no two pages are near-duplicates; the shell only
 * owns layout, structured data, and cross-links.
 *
 * LEGAL: the competitor is named only to say what the page is about. Pages make
 * NO claims about the competitor's product, pricing, features, or quality; the
 * body is evaluation criteria the reader applies, with WeConsult Rx's answer.
 */

export interface Criterion {
  label: string;
  detail: string;
}

export interface CompareSection {
  heading: string;
  body: ReactNode;
}

export interface ComparePageProps {
  slug: string;
  competitor: string;
  title: string;
  description: string;
  h1: string;
  eyebrow?: string;
  /** Answer-first opening: 2-3 sentences that stand alone when quoted. */
  answer: string;
  criteriaHeading: string;
  criteriaLede: string;
  criteria: Criterion[];
  sections: CompareSection[];
  faqs: Faq[];
  related: { to: string; label: string; desc?: string }[];
  cta: { title: string; body: string };
}

export function ComparePage(p: ComparePageProps) {
  const path = comparePath(p.slug);
  const others = COMPARE_PAGES.filter((c) => c.slug !== p.slug);
  const jsonLd = jsonLdGraph(
    breadcrumbNode([
      ["Home", "/"],
      ["Compare", "/compare"],
      [`${p.competitor} alternative`, path],
    ]),
    faqPageJsonLd(p.faqs),
    SOFTWARE_NODE,
    ORGANIZATION_NODE,
  );

  return (
    <>
      <Seo title={p.title} description={p.description} path={path} jsonLd={jsonLd} />

      <Section className="pb-8">
        <Container>
          <nav aria-label="Breadcrumb" className="text-sm text-ink-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-forest-700">
                  Home
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li>
                <Link to="/compare" className="hover:text-forest-700">
                  Compare
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li aria-current="page" className="text-ink-700">
                {p.competitor} alternative
              </li>
            </ol>
          </nav>
          <div className="mt-6 max-w-3xl">
            <Eyebrow>{p.eyebrow ?? "Comparison"}</Eyebrow>
            <Heading level={1} className="mt-3">
              {p.h1}
            </Heading>
            <Lede className="mt-6">{p.answer}</Lede>
            <div className="mt-8 flex flex-wrap gap-3">
              <LinkButton to="/demo">Book a comparison demo</LinkButton>
              <LinkButton to="/platform" variant="secondary">
                See the full workflow
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      <Section tone="soft">
        <Container>
          <Heading level={2}>{p.criteriaHeading}</Heading>
          <Lede className="mt-4">{p.criteriaLede}</Lede>
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {p.criteria.map((c) => (
              <div key={c.label} className="flex gap-4 rounded-2xl border border-border bg-surface p-6">
                <span className="mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full bg-forest-soft text-forest-700">
                  <Check className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <p className="font-semibold text-ink-900">{c.label}</p>
                  <p className="mt-1 text-[0.9375rem] leading-relaxed text-ink-600">{c.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {p.sections.map((s) => (
        <Section key={s.heading}>
          <Container>
            <div className="max-w-3xl">
              <Heading level={2}>{s.heading}</Heading>
              <div className="mt-5 space-y-4 text-[1rem] leading-relaxed text-ink-600 [&_a]:font-medium [&_a]:text-forest-700 [&_a:hover]:underline [&_li]:ml-5 [&_li]:list-disc [&_ul]:space-y-2">
                {s.body}
              </div>
            </div>
          </Container>
        </Section>
      ))}

      <FaqSection items={p.faqs} />

      <RelatedLinks heading="Keep evaluating" items={p.related} />

      <RelatedLinks
        heading="Other comparisons"
        items={others.map((c) => ({
          to: comparePath(c.slug),
          label: `${c.competitor} alternative`,
          desc: c.blurb,
        }))}
      />

      <CtaBand
        title={p.cta.title}
        body={p.cta.body}
        primary={{ to: "/demo", label: "Book a demo" }}
        secondary={{ to: "/pricing", label: "See pricing" }}
      />
    </>
  );
}
